// ChallengeModal — initiate a battle. Two flavours:
//   1. Direct: pick an opponent silo from the BYOK-eligible list
//   2. (V2) Broadcast: leave target empty so anyone can answer the call
// Question source: AI-generated from a topic, OR manual list.
import { useEffect, useState } from "react";
import { X, Sword } from "lucide-react";
import { issueChallenge, listEligibleSilos } from "./api";
import { api } from "@/lib/api";
import { toast } from "sonner";
import BattleTunePill from "./BattleTunePill";

// Provider list. Each entry tags whether the call uses the user's BYOK
// (their own Gemini/OpenAI/Anthropic key) or the platform's direct
// provider key. For cross-owner battles, both silos must use a provider
// that matches each owner's BYOK. For same-owner battles, anything is
// fair game — non-BYOK providers fall back to the platform key.
const SILO_PROVIDERS = [
  { value: "gemini", label: "Gemini 2.5 Pro", model: "gemini-2.5-pro", uses: "byok" },
  { value: "gemini", label: "Gemini 3.5 Flash-Lite", model: "gemini-3.5-flash-lite", uses: "byok" },
  { value: "openai", label: "OpenAI GPT-4o", model: "gpt-4o", uses: "byok" },
  { value: "openai", label: "OpenAI GPT-4o mini", model: "gpt-4o-mini", uses: "byok" },
  { value: "anthropic", label: "Claude Sonnet 4.5", model: "claude-sonnet-4-5-20250929", uses: "byok" },
  { value: "anthropic", label: "Claude Haiku 4.5", model: "claude-haiku-4-5", uses: "byok" },
];

const REFEREE_OPTIONS = [
  { provider: "anthropic", model: "claude-sonnet-4-5-20250929", label: "Claude Sonnet 4.5 (default)" },
  { provider: "openai", model: "gpt-4o", label: "GPT-4o" },
  { provider: "gemini", model: "gemini-2.5-pro", label: "Gemini 2.5 Pro" },
];

export default function ChallengeModal({ onClose, onCreated, initialTargetSiloId = null }) {
  const [step, setStep] = useState("setup"); // setup | confirming
  const [myMines, setMyMines] = useState([]);
  const [opponents, setOpponents] = useState([]);
  const [challengerSiloId, setChallengerSiloId] = useState("");
  const [targetSiloId, setTargetSiloId] = useState(initialTargetSiloId || "");
  const [topic, setTopic] = useState("");
  const [questionSource, setQuestionSource] = useState("ai");
  const [questionCount, setQuestionCount] = useState(3);
  const [manualQuestions, setManualQuestions] = useState(["", "", ""]);
  const [siloProvider, setSiloProvider] = useState(SILO_PROVIDERS[0]);
  const [targetProvider, setTargetProvider] = useState(SILO_PROVIDERS[0]); // same-owner only
  // TUNE — full per-LLM dial state. Each pill stores its own settings in
  // localStorage and pushes the latest into these state holders via onChange.
  // Submit-time: parent reads these and ships them to the backend.
  // Slot keys are deliberate so cross/own modes have distinct memories.
  const [crossTune, setCrossTune] = useState(null);      // both silos same LLM
  const [challengerTune, setChallengerTune] = useState(null);
  const [opponentTune, setOpponentTune] = useState(null);
  const [refereeTune, setRefereeTune] = useState(null);
  // Email-opt-in for cross-owner challenges. Default OFF per Randy.
  const [notifyEmail, setNotifyEmail] = useState(false);
  const [refereeOption, setRefereeOption] = useState(REFEREE_OPTIONS[0]);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // Broadcast + scheduling state
  const [isBroadcast, setIsBroadcast] = useState(true);
  const [scheduleMode, setScheduleMode] = useState("now"); // "now" | "later"
  const [scheduledFor, setScheduledFor] = useState("");
  // Feb 14, 2026 PM session #6 — organizer-chosen inter-round review timer
  // (15s → 5min). Lets the organizer give spectators more time to read each
  // round, or speed-run a sprint. 60s default.
  const [interRoundSeconds, setInterRoundSeconds] = useState(60);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/silos");
        setMyMines(r.data || []);
        if (r.data?.length) setChallengerSiloId(r.data[0].id);
      } catch { /* ignore */ }
      try {
        const ops = await listEligibleSilos();  // now includes own silos with is_yours=true
        setOpponents(ops);
      } catch { /* ignore */ }
    })();
  }, []);

  // Show same-owner notice when both sides belong to the caller
  const challengerSilo = (myMines || []).find(s => s.id === challengerSiloId);
  const targetSilo = (opponents || []).find(s => s.id === targetSiloId);
  const sameOwner = challengerSilo && targetSilo && targetSilo.is_yours;
  const selfVsSelf = challengerSilo && targetSilo && challengerSilo.id === targetSilo.id;

  // Invite-by-email flow ("recruit a non-user" path)
  const [opponentMode, setOpponentMode] = useState("existing"); // "existing" | "invite"
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviting, setInviting] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);
  const sendInvite = async () => {
    if (!challengerSiloId) return toast.error("Pick one of your silos first.");
    if (!inviteEmail.trim()) return toast.error("Add their email.");
    setInviting(true);
    try {
      const r = await api.post("/battles/invite-non-user", {
        inviter_silo_id: challengerSiloId,
        invitee_email: inviteEmail.trim(),
        topic: topic.trim() || null,
        note: note.trim() || null,
        is_broadcast: isBroadcast,
      });
      setInviteResult(r.data);
      toast.success("Invite link ready — copy it and send it to them.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send invite.");
    } finally {
      setInviting(false);
    }
  };

  const submit = async () => {
    if (!challengerSiloId) return toast.error("Pick one of your silos to fight from.");
    if (!targetSiloId) return toast.error("Pick an opponent silo.");
    const cleanManual = manualQuestions.map(q => q.trim()).filter(Boolean);
    if (questionSource === "manual" && cleanManual.length === 0) return toast.error("Add at least one manual question.");
    if (questionSource === "ai" && !topic.trim()) return toast.error("Add a topic so the referee can write questions.");

    setSubmitting(true);
    try {
      // Same-owner battles always private (server enforces it too, but
      // we mirror the rule client-side so the user sees a coherent state).
      const broadcastFinal = sameOwner ? false : isBroadcast;
      let scheduledForIso = null;
      if (scheduleMode === "later" && scheduledFor) {
        // datetime-local inputs are local time; convert to ISO UTC
        try { scheduledForIso = new Date(scheduledFor).toISOString(); } catch { /* invalid */ }
      }
      // Pull tune values: if same-owner, L = challengerTune, R = opponentTune.
      // If cross-owner, both sides use crossTune. Referee always its own.
      const lTune = sameOwner ? (challengerTune || {}) : (crossTune || {});
      const rTune = sameOwner ? (opponentTune || {}) : (crossTune || {});
      const refTune = refereeTune || {};

      // Feb 2026 (Randy pt-silo-wars-provider-mismatch) — HARD FAIL if
      // the tune state is missing. Previously we silently fell back to
      // `siloProvider.value` (which is hardcoded to Gemini in
      // SILO_PROVIDERS[0]), so a Claude-vs-Claude challenge could ship
      // out as Gemini-vs-Claude if the challenger pill's onChange
      // hadn't fired yet (mount race, sameOwner flip, React re-render
      // eating the initial event). No more silent fallbacks — surface
      // the state error so the user can pick a model explicitly.
      const _needChallenger = !lTune.provider || !lTune.model;
      const _needOpponent = sameOwner && (!rTune.provider || !rTune.model);
      const _needReferee = !refTune.provider || !refTune.model;
      if (_needChallenger || _needOpponent || _needReferee) {
        const _missing = [];
        if (_needChallenger) _missing.push(sameOwner ? "Challenger silo" : "Both silos");
        if (_needOpponent) _missing.push("Opponent silo");
        if (_needReferee) _missing.push("Referee");
        toast.error(
          `LLM selector didn't finish loading for: ${_missing.join(", ")}. ` +
          `Click each affected model dropdown and pick a value explicitly, then try Send again.`
        );
        return;
      }

      const battle = await issueChallenge({
        challenger_silo_id: challengerSiloId,
        target_silo_id: targetSiloId,
        topic: topic.trim() || null,
        questions: questionSource === "manual" ? cleanManual : null,
        question_source: questionSource,
        question_count: parseInt(questionCount, 10),
        silo_provider: lTune.provider,
        silo_model: lTune.model,
        referee_provider: refTune.provider,
        referee_model: refTune.model,
        referee_temperature: refTune.temperature ?? null,
        referee_max_tokens: refTune.max_tokens ?? null,
        note: note.trim() || null,
        is_broadcast: broadcastFinal,
        scheduled_for: scheduledForIso,
        notify_email: !sameOwner && notifyEmail,
        challenger_temperature: lTune.temperature ?? null,
        challenger_max_tokens: lTune.max_tokens ?? null,
        target_temperature: sameOwner ? (rTune.temperature ?? null) : null,
        target_max_tokens: sameOwner ? (rTune.max_tokens ?? null) : null,
        target_provider: sameOwner ? rTune.provider : null,
        target_model: sameOwner ? rTune.model : null,
        // Feb 14/17, 2026 — verbosity dials.
        //   answer_style_*   ← contestant pill (silos answer)
        //   question_style_* ← REFEREE pill (referee writes round questions)
        // Splitting these means the silos and the referee each carry only the
        // dial that applies to their actual job.
        answer_style_preset: lTune.answer_style_preset || null,
        answer_style_custom: lTune.answer_style_custom || null,
        question_style_preset: refTune.question_style_preset || null,
        question_style_custom: refTune.question_style_custom || null,
        inter_round_review_seconds: parseInt(interRoundSeconds, 10) || 60,
      });
      toast.success(sameOwner ? "Match queued — starting on schedule." : "Challenge issued. Waiting on opponent.");
      onCreated(battle);
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === "string" ? d
        : Array.isArray(d) ? d.map(x => x?.msg || JSON.stringify(x)).join("; ")
        : (d?.msg || e?.message || "Could not issue challenge.");
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const updateManualQ = (i, v) => {
    const next = [...manualQuestions];
    next[i] = v;
    setManualQuestions(next);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center px-4" onClick={onClose} data-testid="challenge-modal-backdrop">
      <div className="bg-white w-full max-w-2xl rounded-lg shadow-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[#D8D6D2] sticky top-0 bg-white z-10">
          <div className="flex items-center gap-2">
            <Sword size={20} className="text-[#C16E5A]" />
            <h2 className="font-semibold text-lg text-[#1C1B1A]">Issue a battle challenge</h2>
          </div>
          <button onClick={onClose} className="text-[#6B6A68] hover:text-[#1C1B1A]" data-testid="challenge-modal-close"><X size={20} /></button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          {/* Your silo */}
          <Field label="Fight from your silo">
            <select
              value={challengerSiloId}
              onChange={e => setChallengerSiloId(e.target.value)}
              className="w-full border border-[#D8D6D2] rounded-md p-2 text-sm"
              data-testid="challenge-challenger-select"
            >
              {myMines.length === 0 && <option value="">— No silos yet —</option>}
              {myMines.map(s => <option key={s.id} value={s.id}>{s.name || s.site_name || s.slug}</option>)}
            </select>
          </Field>

          {/* Opponent — pick existing OR invite a non-user */}
          <Field label="Who are you fighting?">
            <div className="flex gap-2 mb-2">
              <button
                type="button"
                onClick={() => setOpponentMode("existing")}
                className={`flex-1 border rounded-md py-2 text-sm ${opponentMode === "existing" ? "bg-[#4A4A48] text-white border-[#4A4A48]" : "bg-white text-[#1C1B1A] border-[#D8D6D2]"}`}
                data-testid="challenge-opponent-existing"
              >Existing silo</button>
              <button
                type="button"
                onClick={() => setOpponentMode("invite")}
                className={`flex-1 border rounded-md py-2 text-sm ${opponentMode === "invite" ? "bg-[#4A4A48] text-white border-[#4A4A48]" : "bg-white text-[#1C1B1A] border-[#D8D6D2]"}`}
                data-testid="challenge-opponent-invite"
              >Invite someone new (email)</button>
            </div>
            {opponentMode === "existing" ? (
              <>
                <select
                  value={targetSiloId}
                  onChange={e => setTargetSiloId(e.target.value)}
                  className="w-full border border-[#D8D6D2] rounded-md p-2 text-sm"
                  data-testid="challenge-target-select"
                >
                  <option value="">— Pick a silo —</option>
                  {opponents.length > 0 && opponents.some(s => s.is_yours) && (
                    <optgroup label="Your silos (practice / own-vs-own)">
                      {opponents.filter(s => s.is_yours).map(s => (
                        <option key={s.id} value={s.id}>
                          {s.site_name} · Elo {s.elo}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  {opponents.length > 0 && opponents.some(s => !s.is_yours) && (
                    <optgroup label="Other creators">
                      {opponents.filter(s => !s.is_yours).map(s => (
                        <option key={s.id} value={s.id}>
                          {s.site_name} · {s.owner_name} · Elo {s.elo} · {s.tier}
                        </option>
                      ))}
                    </optgroup>
                  )}
                </select>
                {opponents.length === 0 && (
                  <p className="text-xs text-[#6B6A68] mt-1">No silos with BYOK keys available yet.</p>
                )}
                {selfVsSelf && (
                  <p className="text-xs text-[#A89360] mt-1.5">
                    ⚙ Self-vs-self rehearsal — final verdict will be saved, but no Elo or W/L change.
                  </p>
                )}
                {sameOwner && !selfVsSelf && (
                  <p className="text-xs text-[#A89360] mt-1.5">
                    Both silos are yours. The match will start automatically once you issue the challenge — no acceptance step.
                  </p>
                )}
              </>
            ) : (
              <div className="space-y-2">
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={e => setInviteEmail(e.target.value)}
                  placeholder="opponent@email.com"
                  className="w-full border border-[#D8D6D2] rounded-md p-2 text-sm"
                  data-testid="challenge-invite-email"
                />
                <p className="text-[11px] text-[#6B6A68] leading-relaxed">
                  We'll mint a private invite link tied to this email. <strong>You send it to them
                  yourself</strong> — it lands better coming from you than from us. Once they sign up
                  and build a silo, you can fire off the actual challenge.
                </p>
                {inviteResult?.invite_url && (
                  <div className="p-2.5 rounded-md bg-[#F0F1EE] border hairline space-y-2" data-testid="challenge-invite-link-box">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-[#4A5D4E]">Their invite link</div>
                    <div className="font-mono text-[10px] text-[#1C1B1A] break-all" data-testid="challenge-invite-link">
                      {inviteResult.invite_url}
                    </div>
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(inviteResult.invite_url);
                          toast.success("Invite link copied — send it to them however you like.");
                        } catch { toast.error("Could not copy the link."); }
                      }}
                      data-testid="challenge-invite-copy"
                      className="text-xs px-3 py-1.5 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E]"
                    >
                      Copy invite link
                    </button>
                  </div>
                )}
              </div>
            )}
          </Field>

          {/* Red Elo notice for any same-owner battle */}
          {sameOwner && (
            <p className="text-xs text-[#C16E5A] font-medium" data-testid="challenge-elo-locked-notice">
              Battling yourself disables Elo rankings.
            </p>
          )}

          {/* Broadcast toggle — LIVE vs PRIVATE. Locked OFF for same-owner. */}
          <Field label="Broadcast">
            <div className={`flex items-center gap-3 p-3 rounded-md border ${isBroadcast && !sameOwner ? "bg-[#C16E5A]/5 border-[#C16E5A]/40" : "bg-[#FAFAFA] border-[#D8D6D2]"} ${sameOwner ? "opacity-60" : ""}`}>
              <button
                type="button"
                disabled={sameOwner}
                onClick={() => !sameOwner && setIsBroadcast(b => !b)}
                aria-pressed={isBroadcast && !sameOwner}
                className={`relative w-10 h-6 rounded-full transition ${isBroadcast && !sameOwner ? "bg-[#C16E5A]" : "bg-[#D8D6D2]"} ${sameOwner ? "cursor-not-allowed" : ""}`}
                data-testid="challenge-broadcast-toggle"
              >
                <span className={`absolute top-0.5 ${isBroadcast && !sameOwner ? "left-5" : "left-0.5"} w-5 h-5 bg-white rounded-full shadow transition-all`} />
              </button>
              <div className="flex-1">
                <div className="text-sm font-medium text-[#1C1B1A]">
                  {sameOwner ? "Private (forced — same-owner battle)" : (isBroadcast ? "● LIVE — public arena + replay" : "Private — contestants only")}
                </div>
                <div className="text-[11px] text-[#6B6A68] mt-0.5">
                  {sameOwner
                    ? "Same-owner battles never broadcast. Stays between you and the silo."
                    : (isBroadcast
                      ? "Anyone can watch live and review the replay afterward."
                      : "Only you and your opponent can see the arena and replay.")}
                </div>
              </div>
            </div>
          </Field>

          {/* Schedule — Start now (cross-owner = 15s countdown; own-vs-own = immediate) vs Later */}
          <Field label="When does the battle run?">
            <div className="flex gap-2 mb-2">
              <button
                type="button"
                onClick={() => setScheduleMode("now")}
                className={`flex-1 border rounded-md py-2 text-sm ${scheduleMode === "now" ? "bg-[#4A4A48] text-white border-[#4A4A48]" : "bg-white text-[#1C1B1A] border-[#D8D6D2]"}`}
                data-testid="challenge-schedule-now"
              >Start now{sameOwner ? "" : " (15s countdown)"}</button>
              <button
                type="button"
                onClick={() => setScheduleMode("later")}
                className={`flex-1 border rounded-md py-2 text-sm ${scheduleMode === "later" ? "bg-[#4A4A48] text-white border-[#4A4A48]" : "bg-white text-[#1C1B1A] border-[#D8D6D2]"}`}
                data-testid="challenge-schedule-later"
              >Schedule for later</button>
            </div>
            {scheduleMode === "later" && (
              <input
                type="datetime-local"
                value={scheduledFor}
                onChange={e => setScheduledFor(e.target.value)}
                min={new Date(Date.now() + 60_000).toISOString().slice(0, 16)}
                className="w-full border border-[#D8D6D2] rounded-md p-2 text-sm"
                data-testid="challenge-schedule-input"
              />
            )}
            {scheduleMode === "later" && (
              <p className="text-[11px] text-[#6B6A68] mt-1">
                Heavy-hitter fights — schedule it out so you can rally your audience.
              </p>
            )}
          </Field>

          {/* Question source */}
          <Field label="How should questions be picked?">
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setQuestionSource("ai")}
                className={`flex-1 border rounded-md py-2 text-sm ${questionSource === "ai" ? "bg-[#4A4A48] text-white border-[#4A4A48]" : "bg-white text-[#1C1B1A] border-[#D8D6D2]"}`}
                data-testid="challenge-source-ai"
              >Referee writes them</button>
              <button
                type="button"
                onClick={() => setQuestionSource("manual")}
                className={`flex-1 border rounded-md py-2 text-sm ${questionSource === "manual" ? "bg-[#4A4A48] text-white border-[#4A4A48]" : "bg-white text-[#1C1B1A] border-[#D8D6D2]"}`}
                data-testid="challenge-source-manual"
              >We agreed on a list</button>
            </div>
          </Field>

          {questionSource === "ai" ? (
            <Field label="Topic for the referee to riff on">
              <input
                type="text"
                value={topic}
                onChange={e => setTopic(e.target.value)}
                placeholder="e.g., free will and determinism"
                className="w-full border border-[#D8D6D2] rounded-md p-2 text-sm"
                data-testid="challenge-topic-input"
              />
            </Field>
          ) : (
            <Field label={`Manual questions (up to ${questionCount})`}>
              <div className="space-y-2">
                {manualQuestions.slice(0, questionCount).map((q, i) => (
                  <input
                    key={i}
                    type="text"
                    value={q}
                    onChange={e => updateManualQ(i, e.target.value)}
                    placeholder={`Question ${i + 1}`}
                    className="w-full border border-[#D8D6D2] rounded-md p-2 text-sm"
                    data-testid={`challenge-manual-q-${i}`}
                  />
                ))}
              </div>
            </Field>
          )}

          <Field label="Number of rounds">
            <select
              value={questionCount}
              onChange={e => {
                const n = parseInt(e.target.value, 10);
                setQuestionCount(n);
                if (manualQuestions.length < n) setManualQuestions([...manualQuestions, ...Array(n - manualQuestions.length).fill("")]);
              }}
              className="w-full border border-[#D8D6D2] rounded-md p-2 text-sm"
              data-testid="challenge-question-count"
            >
              {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
            <p className="text-[11px] text-[#6B6A68] mt-1">3 is a good showcase; 5 is the max.</p>
          </Field>

          {/* Feb 14, 2026 PM session #6 — organizer-chosen inter-round
              review timer. Wider window = more time for late spectators
              to drop in between rounds. */}
          <Field label="Time between rounds (review window)">
            <select
              value={interRoundSeconds}
              onChange={e => setInterRoundSeconds(parseInt(e.target.value, 10))}
              className="w-full border border-[#D8D6D2] rounded-md p-2 text-sm"
              data-testid="challenge-inter-round-seconds"
            >
              <option value={15}>15 seconds — sprint</option>
              <option value={30}>30 seconds — quick</option>
              <option value={60}>1 minute — comfortable (default)</option>
              <option value={120}>2 minutes — easy reading</option>
              <option value={180}>3 minutes — deep dive</option>
              <option value={300}>5 minutes — big event (lets spectators arrive)</option>
            </select>
            <p className="text-[11px] text-[#6B6A68] mt-1">
              How long the verdict + next-question screen stays up before the next round auto-fires. Longer windows give late-arriving spectators time to drop in between rounds.
            </p>
          </Field>

          {/* TUNE pill — replaces both the LLM dropdown and the old TUNE block.
              For cross-owner: ONE pill (both silos share). For same-owner: TWO
              pills (challenger + opponent independent). Referee always its own.
              Each pill auto-loads its last-used settings from localStorage. */}
          <Field label={sameOwner ? "Challenger silo will use" : "Both silos will use"}>
            <BattleTunePill
              slotKey={sameOwner ? "challenger" : "cross"}
              variant="contestant"
              label={sameOwner ? "Challenger silo will use" : "Both silos will use"}
              onChange={(v) => sameOwner ? setChallengerTune(v) : setCrossTune(v)}
            />
            <p className="text-[11px] text-[#6B6A68] mt-1">
              {sameOwner
                ? "Personal battles unlock all LLMs — non-Gemini providers use the platform key."
                : "Both silos run identical settings (negotiated parity). BYOK key for Gemini, platform key for OpenAI/Anthropic."}
            </p>
          </Field>

          {/* Same-owner: separate Tune for opponent silo */}
          {sameOwner && (
            <Field label="Opponent silo will use">
              <BattleTunePill
                slotKey="opponent"
                variant="contestant"
                label="Opponent silo will use"
                onChange={setOpponentTune}
              />
              <p className="text-[11px] text-[#A89360] mt-1">
                Pit two LLMs against each other on the same silo content — see which model handles your domain better.
              </p>
            </Field>
          )}

          <Field label="Referee">
            <BattleTunePill
              slotKey="referee"
              variant="referee"
              label="Referee LLM"
              onChange={setRefereeTune}
            />
            <p className="text-[11px] text-[#6B6A68] mt-1">
              The referee scores rounds and writes the final verdict. Temperature: <em>Precise</em> for strict fact-checking, <em>Wild</em> for creative cross-connections.
            </p>
          </Field>

          <Field label="Optional note to your opponent">
            <input
              type="text"
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder="Friendly trash talk goes here…"
              className="w-full border border-[#D8D6D2] rounded-md p-2 text-sm"
              data-testid="challenge-note-input"
            />
          </Field>
        </div>

        {/* Footer */}
        <div className="border-t border-[#D8D6D2] sticky bottom-0 bg-white">
          {/* Cost preview — recomputes from the tune state. The "host" pays
              for their challenger silo + the referee. The opponent (or in
              same-owner battles, the host's other silo) pays for the
              right-side answers. Estimate is a ceiling — actual usage is
              usually lower because tokens often come in below max. */}
          <CostPreview
            sameOwner={sameOwner}
            crossTune={crossTune}
            challengerTune={challengerTune}
            opponentTune={opponentTune}
            refereeTune={refereeTune}
            questionCount={parseInt(questionCount, 10) || 1}
          />
          {/* Email-opt-in — only shown for cross-owner challenges. */}
          {opponentMode === "existing" && !sameOwner && targetSiloId && (
            <label className="flex items-start gap-2 px-4 pt-3 cursor-pointer" data-testid="challenge-notify-email-row">
              <input
                type="checkbox"
                checked={notifyEmail}
                onChange={e => setNotifyEmail(e.target.checked)}
                className="mt-0.5"
                data-testid="challenge-notify-email-checkbox"
              />
              <span className="text-xs text-[#6B6A68] leading-relaxed">
                Email an alert to the opponent. Off by default — the in-app inbox already notifies them.
              </span>
            </label>
          )}
          <div className="p-4 flex justify-end gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm text-[#6B6A68] hover:text-[#1C1B1A]" data-testid="challenge-modal-cancel">Cancel</button>
            {opponentMode === "invite" ? (
              inviteResult ? (
                <button
                  onClick={onClose}
                  className="px-5 py-2 bg-[#4A5D4E] text-white text-sm rounded-md"
                  data-testid="challenge-invite-done"
                >Invite link ready — close</button>
              ) : (
                <button
                  onClick={sendInvite}
                  disabled={inviting}
                  className="px-5 py-2 bg-[#4A4A48] text-white text-sm rounded-md hover:bg-[#5A5A58] disabled:opacity-50"
                  data-testid="challenge-invite-send"
                >
                  {inviting ? "Creating…" : "Create invite link"}
                </button>
              )
            ) : (
              <button
                onClick={submit}
                disabled={submitting}
                className="px-5 py-2 bg-[#4A4A48] text-white text-sm rounded-md hover:bg-[#5A5A58] disabled:opacity-50"
                data-testid="challenge-modal-submit"
              >
                {submitting ? "Issuing…" : "Issue challenge"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-wider text-[#6B6A68] mb-1.5">{label}</label>
      {children}
    </div>
  );
}

// CostPreview — rough, slightly-conservative estimate of what this match
// will cost. Uses the LLM pricing baked into BattleTunePill's registry.
// "Host" pays for: challenger silo + referee.
// "Opponent" pays for: their own silo's answers.
// In same-owner mode the host pays for everything (single account).
//
// We use the per-1M token pricing from each provider, treating max_tokens
// as the output ceiling (rare to actually hit but worst-case visibility
// is the point) and 1500 input tokens per turn (system prompt + question
// + retrieved context — typical for our silos).
const PRICING = {
  // per-1M tokens (input, output) USD
  "gemini-2.5-pro":              { in: 1.25, out: 10 },
  // Nov 2026 — these were Gemini 1.5 Flash rates carried forward onto 2.5
  // Flash; the backend table was corrected in Oct 2026 but this copy was not.
  "gemini-3.5-flash-lite":       { in: 0.30, out: 2.50 },
  "gemini-2.5-flash":            { in: 0.30, out: 2.50 },
  "claude-sonnet-4-5-20250929":  { in: 3, out: 15 },
  "claude-haiku-4-5":            { in: 0.80, out: 4 },
  "gpt-4o":                      { in: 5, out: 15 },
  "gpt-4o-mini":                 { in: 0.15, out: 0.60 },
};
const ASSUMED_INPUT_TOKENS = 1500;

function estCents({ model, max_tokens }, q_count) {
  const p = PRICING[model];
  if (!p) return 0;
  const out = (max_tokens || 4096);
  const inT = ASSUMED_INPUT_TOKENS;
  const dollars = q_count * (inT * p.in + out * p.out) / 1_000_000;
  return Math.round(dollars * 100);
}

function fmt$(cents) {
  if (cents < 1) return "<$0.01";
  return `$${(cents / 100).toFixed(2)}`;
}

function CostPreview({ sameOwner, crossTune, challengerTune, opponentTune, refereeTune, questionCount }) {
  // Resolve which tune drives each side
  const lTune = sameOwner ? challengerTune : crossTune;
  const rTune = sameOwner ? opponentTune : crossTune;
  const refTune = refereeTune;

  if (!lTune || !rTune || !refTune) {
    return (
      <div className="px-4 pt-3 text-[11px] text-[#6B6A68]">
        Loading cost estimate…
      </div>
    );
  }

  const lCents = estCents(lTune, questionCount);
  const rCents = estCents(rTune, questionCount);
  const refCents = estCents(refTune, questionCount);
  const hostCents = lCents + refCents;
  const oppCents = rCents;
  const totalCents = hostCents + (sameOwner ? oppCents : 0);

  return (
    <div className="px-4 pt-3" data-testid="challenge-cost-preview">
      <div className="bg-[#FAF8F4] border border-[#E5E2DD] rounded-md p-3">
        <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.18em] text-[#A89360] mb-2">
          <span>Estimated cost (max ceiling)</span>
          <span className="text-[#6B6A68] normal-case tracking-normal">{questionCount} {questionCount === 1 ? "round" : "rounds"}</span>
        </div>
        <div className="grid grid-cols-3 gap-2 text-xs">
          <div className="text-center p-2 bg-white rounded border border-[#E5E2DD]" data-testid="cost-host">
            <div className="text-[10px] text-[#6B6A68] mb-0.5">Host pays</div>
            <div className="text-[#1C1B1A] font-medium tabular-nums">{fmt$(hostCents)}</div>
            <div className="text-[9px] text-[#6B6A68] mt-0.5">silo + referee</div>
          </div>
          <div className="text-center p-2 bg-white rounded border border-[#E5E2DD]" data-testid="cost-opponent">
            <div className="text-[10px] text-[#6B6A68] mb-0.5">{sameOwner ? "Other silo" : "Opponent pays"}</div>
            <div className="text-[#1C1B1A] font-medium tabular-nums">{fmt$(oppCents)}</div>
            <div className="text-[9px] text-[#6B6A68] mt-0.5">{sameOwner ? "you also" : "their silo"}</div>
          </div>
          <div className="text-center p-2 bg-[#4A5D4E]/5 rounded border border-[#4A5D4E]/20" data-testid="cost-total">
            <div className="text-[10px] text-[#4A5D4E] mb-0.5">{sameOwner ? "Total (you)" : "Total (combined)"}</div>
            <div className="text-[#1C1B1A] font-semibold tabular-nums">{fmt$(totalCents)}</div>
            <div className="text-[9px] text-[#6B6A68] mt-0.5">worst case</div>
          </div>
        </div>
        <p className="text-[10px] text-[#6B6A68] mt-2 leading-relaxed">
          Ceiling estimate based on max-output settings — actual usually lower.
          Referee paid by the host. Dial Max output down to reduce cost.
        </p>
      </div>
    </div>
  );
}

